package com.tfc.care.tfccare

import com.tfc.care.tfccare.domain.model.*
import com.tfc.care.tfccare.domain.repository.StaffFirebaseService
import com.tfc.care.tfccare.utils.ApiResult
import com.tfc.care.tfccare.utils.Logger
import com.tfc.care.tfccare.utils.getCurrentDate
import com.tfc.care.tfccare.utils.getCurrentDateAndTime
import com.tfc.care.tfccare.utils.getCurrentMonth
import com.tfc.care.tfccare.utils.getPreviousMonth
import com.tfc.care.tfccare.utils.runCatchingSuspend
import com.tfc.care.tfccare.utils.toBooleanSafe
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.await
import kotlinx.coroutines.channels.awaitClose
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.callbackFlow
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlin.js.json

class WebStaffFirebaseService : StaffFirebaseService {

    private val auth = FirebaseAuth.getAuth()
    private val database = FirebaseDatabase.getDatabase()
    private val storage = FirebaseStorage.getStorage()
    private val functions = FirebaseFunctions.getFunctions()

    private val jsonConfig = Json {
        ignoreUnknownKeys = true
        isLenient = true
        encodeDefaults = true
    }
    private val _currentStaffFlow = MutableStateFlow<Staff?>(null)
    private var currentStaff: Staff? = null
        set(value) {
            field = value
            _currentStaffFlow.value = value
        }

    init {
        Logger.d("WebStaffFirebaseService: init started")

        // Wait for persistence to be set before continuing initialization
        GlobalScope.launch {
            try {
                Logger.d("WebStaffFirebaseService: Attempting to set persistence")
                FirebaseAuth.setPersistence(auth, FirebaseAuth.browserLocalPersistence).await()
                Logger.d("WebStaffFirebaseService: Firebase persistence set successfully")
            } catch (e: Exception) {
                Logger.e("WebStaffFirebaseService: Failed to set Firebase persistence: ${e.message}")
            }
        }
    }

    override suspend fun signInStaff(email: String, password: String): ApiResult<Staff> {
        return runCatchingSuspend {
            val trimmedEmail = email.trim()
            val trimmedPassword = password.trim()
            Logger.d("Firebase Request: signInStaff(email='$trimmedEmail')")
            Logger.d("Email length: ${trimmedEmail.length}")
            val authResult = FirebaseAuth.signInWithEmailAndPassword(auth, trimmedEmail, trimmedPassword).await()

            val staffId: String = authResult.user.uid.toString()

            // Verify staff user
            val staffSnapshot = FirebaseDatabase.get(FirebaseDatabase.ref(database, "staff/$staffId")).await()
            val staffData = staffSnapshot.`val`()

            // Manual parsing of Staff with Engagement
            val staff = parseStaff(staffId, staffData)

            if (!staff.isValid) {
                Logger.d("Firebase Response: signInStaff - Unauthorized (staff.isValid is not true)")
                FirebaseAuth.signOut(auth).await()
                throw Exception("User is not authorized as staff")
            }

            currentStaff = staff.copy(id = staffId)

            // Update online status
            updateOnlineStatus(true)

            Logger.d("Firebase Response: signInStaff - Success for staffId=$staffId")
            currentStaff!!
        }
    }

    private fun parseStaff(id: String, data: dynamic): Staff {
        if (data == null) return Staff(id = id)

        /*  val servedCustomers = mutableListOf<String>()
          val existingServed = data.servedCustomers
          if (existingServed != null) {
              val length = existingServed.length as? Int ?: 0
              for (i in 0 until length) {
                  (existingServed[i] as? String)?.let { servedCustomers.add(it) }
              }
          }*/

        val engagementData = data.engagement
        val engagement: StaffEngagement? = if (engagementData != null) {
            StaffEngagement(
                dayCount = (engagementData.dayCount as? Int) ?: 0,
                monthCount = (engagementData.monthCount as? Int) ?: 0,
                previousMonthCount = (engagementData.previousMonthCount as? Int) ?: 0,
                lastUpdatedDay = (engagementData.lastUpdatedDay as? String) ?: "",
                lastUpdatedMonth = (engagementData.lastUpdatedMonth as? String) ?: ""
            )
        } else null

        return Staff(
            id = id,
            isValid = (data.isValid as? Boolean) ?: false,
            userName = data.userName as? String,
            online = (data.online as? Boolean) ?: false,
            emailId = (data.emailId as? String) ?: "",
            engagement = engagement
        )
    }

    override suspend fun signOut() {
        clearFirebaseAuthFromStorage()
        Logger.d("WebStaffFirebaseService: Request: signOut")
        currentStaff?.let {
            updateOnlineStatus(false)
        }
        FirebaseAuth.signOut(auth).await()
        currentStaff = null
        Logger.d("WebStaffFirebaseService: Response: signOut - Success")
    }

    private fun clearFirebaseAuthFromStorage() {
        val storage = kotlinx.browser.localStorage
        val keysToRemove = mutableListOf<String>()

        for (i in 0 until storage.length) {
            val key = storage.key(i) ?: continue
            if (key.startsWith("firebase:")) {
                keysToRemove.add(key)
            }
        }

        keysToRemove.forEach { storage.removeItem(it) }
    }

    override fun getCurrentStaff(): Staff? = currentStaff

    override suspend fun getSavedStaff(): Flow<Staff?> {
        Logger.d("Firebase Request: getSavedStaff")
        FirebaseAuth.setPersistence(auth, FirebaseAuth.browserLocalPersistence).await()
        Logger.d("Firebase Request: getSavedStaff set persistence done ")

        return callbackFlow {
            val unsubscribe = FirebaseAuth.onAuthStateChanged(auth) { user ->
                if (user != null) {
                    val staffId = user.uid.toString()
                    Logger.d("Firebase Request user is not null: $staffId")

                    GlobalScope.launch {
                        try {
                            val staffSnapshot =
                                FirebaseDatabase.get(FirebaseDatabase.ref(database, "staff/$staffId")).await()
                            val staffData = staffSnapshot.`val`()
                            val staff = parseStaff(staffId, staffData)

                            if (staff.isValid) {
                                currentStaff = staff
                                trySend(staff)
                            } else {
                                Logger.d("Firebase Request: staff invalid, signing out")
                                FirebaseAuth.signOut(auth).await()
                                trySend(null)
                            }
                        } catch (e: Exception) {
                            Logger.e("Error fetching saved staff: ${e.message}")
                            trySend(null)
                        }
                    }
                } else {
                    Logger.d("Firebase Request: user null")
                    trySend(null)
                }
            }
            awaitClose {
                Logger.d("Firebase Request: unsubscribe")
                unsubscribe()
            }
        }
    }

    private val userCache = mutableMapOf<String, dynamic>()

    override fun observeCurrentStaff(): Flow<Staff?> = _currentStaffFlow

    override fun observeConversations(): Flow<List<Conversation>> = callbackFlow {
        Logger.d("Firebase Request: observeConversations")
        val conversationsRef = FirebaseDatabase.ref(database, "conversations")

        val unsubscribe = FirebaseDatabase.onValue(conversationsRef) { snapshot ->
            Logger.d("Firebase Response: observeConversations - New snapshot received")
            val children = mutableListOf<Pair<String, dynamic>>()

            snapshot.forEach { child ->
                children.add(child.key as String to child.`val`())
                false // continue iteration
            }

            // We need to fetch user data for each conversation.
            // Since this is a callback, we'll launch a coroutine to fetch user details.
            launch {
                val results = children.map { (userId, convData) ->
                    val metadata = convData.metadata
                    val cachedUser = userCache[userId]

                    if (cachedUser != null) {
                        Conversation(
                            userId = userId,
                            userName = (cachedUser.name as? String) ?: "",
                            companyName = (cachedUser.companyName as? String) ?: "",
                            userEmail = (cachedUser.email as? String) ?: "",
                            lastMessage = (metadata?.lastMessage as? String) ?: "",
                            lastMessageTime = (metadata?.lastMessageTime as? String) ?: "",
                            unreadCount = (metadata?.unreadCount as? Int) ?: 0,
                            userNumber = (cachedUser.number as? String) ?: "",
                            userImg = (cachedUser.img as? String) ?: "",
                            fcmToken = (cachedUser.fcmToken as? String) ?: "",
                            lastSeen = (cachedUser.lastSeen as? String) ?: "",
                            isOnline = (cachedUser.online as? Any).toBooleanSafe()
                        )
                    } else {
                        // Create a placeholder conversation while fetching user data
                        Conversation(
                            userId = userId,
                            userName = "Loading...",
                            companyName = "",
                            userEmail = "",
                            lastMessage = (metadata?.lastMessage as? String) ?: "",
                            lastMessageTime = (metadata?.lastMessageTime as? String) ?: "",
                            unreadCount = (metadata?.unreadCount as? Int) ?: 0,
                            userNumber = "",
                            userImg = "",
                            fcmToken = "",
                            lastSeen = "",
                            isOnline = false
                        )
                    }
                }.sortedByDescending { it.lastMessageTime }

                Logger.d("Firebase Processed: observeConversations - Emitting ${results.size} conversations (with placeholders/cache)")
                trySend(results)

                // Now fetch missing user details in parallel
                val missingUserIds = children.map { it.first }.filter { !userCache.containsKey(it) }
                if (missingUserIds.isNotEmpty()) {
                    missingUserIds.map { userId ->
                        launch {
                            try {
                                val userSnapshot =
                                    FirebaseDatabase.get(FirebaseDatabase.ref(database, "users/$userId")).await()
                                if (userSnapshot.exists()) {
                                    val userData = userSnapshot.`val`()
                                    userCache[userId] = userData

                                    // Trigger a re-emit by finding the conversation in the current list and updating it
                                    // However, since we are inside onValue, any change to the database will trigger it again.
                                    // But user data is in a DIFFERENT node. So we should manually re-process the current children.
                                    val currentChildren = children.toList() // snapshot of current children
                                    val updatedResults = currentChildren.map { (uid, cData) ->
                                        val mdata = cData.metadata
                                        val uData = userCache[uid]
                                        if (uData != null) {
                                            Conversation(
                                                userId = uid,
                                                userName = (uData.name as? String) ?: "",
                                                companyName = (uData.companyName as? String) ?: "",
                                                userEmail = (uData.email as? String) ?: "",
                                                lastMessage = (mdata?.lastMessage as? String) ?: "",
                                                lastMessageTime = (mdata?.lastMessageTime as? String) ?: "",
                                                unreadCount = (mdata?.unreadCount as? Int) ?: 0,
                                                userNumber = (uData.number as? String) ?: "",
                                                userImg = (uData.img as? String) ?: "",
                                                fcmToken = (uData.fcmToken as? String) ?: "",
                                                lastSeen = (uData.lastSeen as? String) ?: "",
                                                isOnline = (uData.online as? Any).toBooleanSafe()
                                            )
                                        } else {
                                            Conversation(
                                                userId = uid,
                                                userName = "Loading...",
                                                companyName = "",
                                                userEmail = "",
                                                lastMessage = (mdata?.lastMessage as? String) ?: "",
                                                lastMessageTime = (mdata?.lastMessageTime as? String) ?: "",
                                                unreadCount = (mdata?.unreadCount as? Int) ?: 0,
                                                userNumber = "",
                                                userImg = "",
                                                fcmToken = "",
                                                lastSeen = "",
                                                isOnline = false
                                            )
                                        }
                                    }.sortedByDescending { it.lastMessageTime }
                                    trySend(updatedResults)
                                }
                            } catch (e: Exception) {
                                Logger.e("Error fetching user $userId: ${e.message}")
                            }
                        }
                    }
                }
            }
        }

        awaitClose {
            Logger.d("Firebase Cleanup: observeConversations")
            unsubscribe()
        }
    }

    override fun observeMessages(userId: String): Flow<List<Message>> = callbackFlow {
        Logger.d("Firebase Request: observeMessages(userId=$userId)")
        val messagesRef = FirebaseDatabase.ref(database, "conversations/$userId/messages")

        val unsubscribe = FirebaseDatabase.onValue(messagesRef) { snapshot ->
            Logger.d("Firebase Response: observeMessages(userId=$userId) - New snapshot received")
            val messages = mutableListOf<Message>()

            snapshot.forEach { msgSnapshot ->
                try {
                    val messageId = msgSnapshot.key as String
                    val msgVal = msgSnapshot.`val`()
                    val msgData: String = JSON.stringify(msgVal)
                    val msgObject: JsonObject = Json.parseToJsonElement(msgData).jsonObject

                    val read: Boolean = try {
                        msgObject["read"]?.jsonPrimitive?.booleanOrNull ?: false
                    } catch (e: Exception) {
                        (msgObject["read"]?.jsonPrimitive?.intOrNull ?: 0).toBooleanSafe()
                    }

                    val senderTypeStr = msgObject["senderType"]?.jsonPrimitive?.contentOrNull ?: "STAFF"
                    val senderType = if (senderTypeStr == SenderType.STAFF.name) SenderType.STAFF else SenderType.USER

                    val messageTypeStr = msgObject["messageType"]?.jsonPrimitive?.contentOrNull ?: "TEXT"
                    val messageType =
                        if (messageTypeStr == MessageType.IMAGE.name) MessageType.IMAGE else MessageType.TEXT

                    val message = Message(
                        id = messageId,
                        text = msgObject["text"]?.jsonPrimitive?.contentOrNull ?: "",
                        senderId = msgObject["senderId"]?.jsonPrimitive?.contentOrNull ?: "",
                        senderType = senderType,
                        timestamp = msgObject["timestamp"]?.jsonPrimitive?.contentOrNull ?: "",
                        read = read,
                        name = msgObject["name"]?.jsonPrimitive?.contentOrNull ?: "",
                        imageUrl = msgObject["imageUrl"]?.jsonPrimitive?.contentOrNull ?: "",
                        imageLocalPath = msgObject["imageLocalPath"]?.jsonPrimitive?.contentOrNull ?: "",
                        messageType = messageType,
                    )

                    messages.add(message)

                    // Auto-mark user messages as read
                    if (senderType == SenderType.USER && !read) {
                        Logger.d("Firebase Action: Mark message $messageId as read for userId=$userId")
                        FirebaseDatabase.update(
                            FirebaseDatabase.ref(database, "conversations/$userId/messages/$messageId"),
                            json("read" to true)
                        )
                    }
                } catch (e: Exception) {
                    Logger.e("Error parsing message: ${e.message}")
                }
                false
            }

            Logger.d("Firebase Processed: observeMessages(userId=$userId) - Emitting ${messages.size} messages")
            trySend(messages.sortedBy { it.timestamp })
        }

        awaitClose {
            Logger.d("Firebase Cleanup: observeMessages(userId=$userId)")
            unsubscribe()
        }
    }

    override suspend fun sendMessage(
        userId: String,
        messageText: String
    ): ApiResult<Unit> {
        return runCatchingSuspend {
            Logger.d("Firebase Request: sendMessage(userId=$userId, text=$messageText)")
            val staff = currentStaff ?: throw Exception("Not authenticated")

            val timestamp = getCurrentDateAndTime()
            val newMsgRef = FirebaseDatabase.push(FirebaseDatabase.ref(database, "conversations/$userId/messages"))

            val messageId = newMsgRef.key ?: ""

            val message = Message(
                id = messageId,
                text = messageText,
                senderId = staff.id,
                senderType = SenderType.STAFF,
                timestamp = timestamp,
                read = false,
                messageType = MessageType.TEXT,
                name = staff.userName ?: "Staff",
            )

            val messageJson = jsonConfig.encodeToString(message)
            val messageObj = JSON.parse<dynamic>(messageJson)

            // Send message
            FirebaseDatabase.set(newMsgRef, messageObj).await()

            // Update metadata
            val metadata = json(
                "unreadCount" to 0,
                "lastMessage" to messageText,
                "lastMessageTime" to timestamp
            )

            FirebaseDatabase.update(FirebaseDatabase.ref(database, "conversations/$userId/metadata"), metadata).await()
            sendPushNotification(userId)

            trackEngagement(userId)

            Logger.d("Firebase Response: sendMessage - Success for userId=$userId")
        }
    }

    private suspend fun trackEngagement(userId: String) {
        try {
            val staff = currentStaff ?: return
            val staffRef = FirebaseDatabase.ref(database, "staff/${staff.id}")
            val staffSnapshot = FirebaseDatabase.get(staffRef).await()
            if (staffSnapshot.exists()) {
                val staffData = staffSnapshot.`val`()

                val today = getCurrentDate()
                val thisMonth = getCurrentMonth()
                val prevMonth = getPreviousMonth()

                // Engagement Object
                val engagementData = staffData.engagement
                val dayCustomers = mutableListOf<String>()

                var monthCustomers = 0L
                var prevMonthCustomers = 0L

                var lastUpdatedDay = ""
                var lastUpdatedMonth = ""

                if (engagementData != null) {

                    lastUpdatedDay = (engagementData.lastUpdatedDay as? String) ?: ""
                    lastUpdatedMonth = (engagementData.lastUpdatedMonth as? String) ?: ""

                    monthCustomers = (engagementData.monthCount as? Long) ?: 0L
                    prevMonthCustomers = (engagementData.previousMonthCount as? Long) ?: 0L

                    // Handle Day reset
                    if (lastUpdatedDay == today) {
                        val existing = engagementData.servedCustomersDay
                        if (existing != null) {
                            val len = existing.length as? Int ?: 0
                            for (i in 0 until len) {
                                (existing[i] as? String)?.let { dayCustomers.add(it) }
                            }
                        }
                    }else{
                        monthCustomers+=dayCustomers.size
                        dayCustomers.clear()
                    }

                    if (lastUpdatedMonth == prevMonth) {
                        // Month changed from last month to this month
                        // Move last month's data to previous month
                        prevMonthCustomers += monthCustomers
                        monthCustomers = 0L
                    }
                }

                if (!dayCustomers.contains(userId)) {
                    dayCustomers.add(userId)
                }

                val newEngagement = json(
                    "dayCount" to dayCustomers.size,
                    "monthCount" to monthCustomers.toInt(),
                    "previousMonthCount" to prevMonthCustomers.toInt() ,
                    "servedCustomersDay" to dayCustomers.toTypedArray(),
                    "lastUpdatedDay" to today,
                    "lastUpdatedMonth" to thisMonth
                )

                val updates = json(
                    "engagement" to newEngagement,
                )
                FirebaseDatabase.update(staffRef, updates).await()

                // Update local currentStaff
                val engagementModel = StaffEngagement(
                    dayCount = dayCustomers.size,
                    monthCount = monthCustomers.toInt(),
                    previousMonthCount = prevMonthCustomers.toInt(),
                    lastUpdatedDay = today,
                    lastUpdatedMonth = thisMonth
                )
                currentStaff = staff.copy(
                    engagement = engagementModel
                )
                Logger.d("Engagement Updated for Staff ${staff.id}. Day: ${dayCustomers.size}, Month: $monthCustomers, Prev: $prevMonthCustomers")
            }
        } catch (e: Exception) {
            Logger.e("Error tracking engagement: ${e.message}")
        }
    }

    override suspend fun sendImageMessage(
        userId: String,
        imageData: dynamic,
        caption: String
    ): ApiResult<Unit> {
        return runCatchingSuspend {
            Logger.d("Firebase Request: sendImageMessage(userId=$userId, caption=$caption, size=${imageData.size} bytes)")
            val staff = currentStaff ?: throw Exception("Not authenticated")
            val imageId = generateId()
            val imageName = "chat_images/$userId/$imageId.jpg"

            // Upload image
            Logger.d("Firebase Request: uploadBytes(path=$imageName)")
            val storageRef = FirebaseStorage.ref(storage, imageName)
            FirebaseStorage.uploadBytes(storageRef, imageData).await()

            val imageUrl = FirebaseStorage.getDownloadURL(storageRef).await()
            Logger.d("Firebase Response: uploadBytes - Success, imageUrl=$imageUrl")

            val timestamp = getCurrentDateAndTime()
            val newMsgRef = FirebaseDatabase.push(FirebaseDatabase.ref(database, "conversations/$userId/messages"))

            val messageId = newMsgRef.key ?: ""
            val message = Message(
                id = messageId,
                text = caption,
                senderId = staff.id,
                senderType = SenderType.STAFF,
                timestamp = timestamp,
                read = false,
                imageUrl = imageUrl,
                messageType = MessageType.IMAGE,
                name = staff.userName ?: "Staff"
            )
            Logger.d("Firebase Response: message created ${message}")

            val messageJson = jsonConfig.encodeToString(message)
            val messageObj = JSON.parse<dynamic>(messageJson)

            // Send message
            FirebaseDatabase.set(newMsgRef, messageObj).await()
            Logger.d("Firebase Response: message updated ")

            // Update metadata
            val lastMessageText = caption.ifEmpty { "Image" }
            val metadata = json(
                "unreadCount" to 0,
                "lastMessage" to lastMessageText,
                "lastMessageTime" to timestamp
            )

            FirebaseDatabase.update(FirebaseDatabase.ref(database, "conversations/$userId/metadata"), metadata).await()

            // Send push notification
            sendPushNotification(userId)
            trackEngagement(userId)
            Logger.d("Firebase Response: sendImageMessage - Success for userId=$userId")
        }
    }

    override suspend fun markConversationAsRead(userId: String): ApiResult<Unit> {
        return runCatchingSuspend {
            Logger.d("Firebase Request: markConversationAsRead(userId=$userId)")
            // Reset unread count
            FirebaseDatabase.update(
                FirebaseDatabase.ref(database, "conversations/$userId/metadata"),
                json("unreadCount" to 0)
            ).await()
            Logger.d("Firebase Response: markConversationAsRead - Success for userId=$userId")
        }
    }


    override suspend fun updateOnlineStatus(online: Boolean): ApiResult<Unit> {
        return runCatchingSuspend {
            val staff = currentStaff ?: return@runCatchingSuspend
            Logger.d("Firebase Request: updateOnlineStatus(online=$online) for staffId=${staff.id}")
            val timestamp = getCurrentDateAndTime()
            Logger.d("Firebase Action: updateOnlineStatus - Setting lastSeen=$timestamp for staffId=${staff.id}")

            val updates = json(
                "online" to online,
                "lastSeen" to timestamp
            )

            FirebaseDatabase.update(FirebaseDatabase.ref(database, "staff/${staff.id}"), updates).await()
            Logger.d("Firebase Response: updateOnlineStatus - Success")
        }
    }

    override fun observeStaff(staffId: String): Flow<Staff?> = callbackFlow {
        val staffRef = FirebaseDatabase.ref(database, "staff/$staffId")
        val unsubscribe = FirebaseDatabase.onValue(staffRef) { snapshot ->
            if (snapshot.exists()) {
                val staffData = snapshot.`val`()
                trySend(parseStaff(staffId, staffData))
            } else {
                trySend(null)
            }
        }
        awaitClose { unsubscribe() }
    }

    private suspend fun sendPushNotification(userId: String) {
        try {
            Logger.d("Firebase Request: sendPushNotification(userId=$userId) - Fetching user data")
            val userSnapshot = FirebaseDatabase.get(FirebaseDatabase.ref(database, "users/$userId")).await()
            if (userSnapshot.exists()) {
                val userData = userSnapshot.`val`()
                val isOnline = (userData.online as? Any).toBooleanSafe()
                val fcmToken: String? = userData.fcmToken as? String

                if (!isOnline && !fcmToken.isNullOrEmpty()) {
                    Logger.d("Firebase Action: sendPushNotification - Calling Cloud Function for token: $fcmToken")
                    val sendPush = FirebaseFunctions.httpsCallable(functions, "sendNotification")
                    val data = json(
                        "token" to fcmToken,
                        "title" to "New Message",
                        "body" to "You have a new message from TFC Care staff"
                    )
                    sendPush(data).await()
                    Logger.d("Firebase Response: sendPushNotification - Cloud Function called successfully: $fcmToken and data is $data")
                } else {
                    Logger.d("Firebase Action: sendPushNotification - Skip (isOnline=$isOnline, hasToken=${!fcmToken.isNullOrEmpty()})")
                }
            } else {
                Logger.d("Firebase Response: sendPushNotification - User $userId does not exist")
            }
        } catch (e: Exception) {
            Logger.e("Firebase Response: sendPushNotification - Error: ${e.message}")
        }
    }

    private fun generateId(): String {
        return (0..15).map { (('a'..'z') + ('0'..'9')).random() }.joinToString("")
    }
}